import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiX,
  FiCalendar,
  FiAlertCircle,
  FiCheckCircle,
  FiBox,
  FiLoader,
  FiChevronDown,
} from "react-icons/fi";

const defaultFormData = {
  bookingBLNo: "",
  date: "",
  containerNumber: "",
  vesselName: "",
  origin: "China",
  destination: "UK",
  status: "In Transit",
  customCleared: false,
  containerType: "40ft HC",
  shipper: "",
  consignee: "Meraki",
  customerId: "",
  expectedArrival: "",
  deliveryBookedOn: "",
  mainGoods: "",
  description: "",
};

const statusOptions = [
  "In Transit",
  "Arrived at Port",
  "Delivered",
  "Departed from Origin Port",
];

const containerTypeOptions = [
  "20ft Standard",
  "40ft Standard",
  "40ft HC",
  "45ft High Cube",
  "Reefer 20ft",
  "Reefer 40ft",
  "Open Top",
  "Flat Rack",
];

const originOptions = ["China", "Turkey"];
const destinationOptions = ["UK", "Ireland"];
const consigneeOptions = ["Meraki", "Serena"];

const getTodayInputDate = () => {
  return new Date().toISOString().split("T")[0];
};

const normalizeDateForInput = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.split("T")[0];
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().split("T")[0];
};

const getErrorMessage = (err) => {
  return (
    err?.response?.data?.message ||
    err?.customMessage ||
    err?.message ||
    "Something went wrong. Please try again."
  );
};

const safeJsonParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getRoleFromToken = () => {
  try {
    const token = localStorage.getItem("token");

    if (!token || !token.includes(".")) return "";

    const payload = JSON.parse(atob(token.split(".")[1]));

    return payload?.role || payload?.userRole || "";
  } catch {
    return "";
  }
};

const getCurrentRole = () => {
  const possibleUserKeys = [
    "user",
    "authUser",
    "currentUser",
    "userData",
    "admin",
  ];

  for (const key of possibleUserKeys) {
    const user = safeJsonParse(localStorage.getItem(key));

    if (user?.role) return user.role;
    if (user?.userRole) return user.userRole;
    if (user?.type) return user.type;
  }

  return localStorage.getItem("role") || getRoleFromToken() || "";
};

const checkIsSuperAdmin = () => {
  return String(getCurrentRole()).toLowerCase() === "super_admin";
};

const isKnownOption = (value, options) => {
  return options.includes(value);
};

function InlineMessage({ type = "error", message, onClose }) {
  if (!message) return null;

  const isError = type === "error";

  return (
    <div
      className={`mb-5 flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
        isError
          ? "border-red-100 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
          : "border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
      }`}
    >
      <div
        className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl ${
          isError
            ? "bg-red-100 dark:bg-red-900"
            : "bg-emerald-100 dark:bg-emerald-900"
        }`}
      >
        {isError ? <FiAlertCircle /> : <FiCheckCircle />}
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-semibold">
          {isError ? "Action not allowed" : "Success"}
        </p>

        <p className="mt-0.5 leading-5 text-slate-600 dark:text-slate-300">
          {message}
        </p>
      </div>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-white/70 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <FiX />
        </button>
      ) : null}
    </div>
  );
}

function SelectWithCustom({
  label,
  value,
  options,
  onChange,
  inputClass,
  selectClass,
  labelClass,
  placeholder,
}) {
  const inputRef = useRef(null);

  const [mode, setMode] = useState(() => {
    return value && !isKnownOption(value, options) ? "custom" : "select";
  });

  const [customValue, setCustomValue] = useState(() => {
    return value && !isKnownOption(value, options) ? value : "";
  });

  useEffect(() => {
    if (value && !isKnownOption(value, options)) {
      setMode("custom");
      setCustomValue(value);
      return;
    }

    if (value && isKnownOption(value, options)) {
      setMode("select");
      setCustomValue("");
    }
  }, [value, options]);

  useEffect(() => {
    if (mode === "custom") {
      window.setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  }, [mode]);

  const handleCustomClick = () => {
    setMode("custom");
    setCustomValue("");
    onChange("");
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>

      {mode === "select" ? (
        <div className="relative">
          <select
            value={value && isKnownOption(value, options) ? value : options[0]}
            onChange={(e) => {
              if (e.target.value === "__custom__") {
                handleCustomClick();
                return;
              }

              onChange(e.target.value);
            }}
            className={selectClass}
          >
            {options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}

            <option value="__custom__">Custom Entry</option>
          </select>

          <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={customValue}
            onChange={(e) => {
              setCustomValue(e.target.value);
              onChange(e.target.value);
            }}
            className={inputClass}
            placeholder={placeholder}
            autoComplete="off"
          />

          <button
            type="button"
            onClick={() => {
              setMode("select");
              setCustomValue("");
              onChange(options[0]);
            }}
            className="shrink-0 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            List
          </button>
        </div>
      )}
    </div>
  );
}

function DateField({
  label,
  value,
  onChange,
  required = false,
  error = "",
  labelClass,
}) {
  const inputRef = useRef(null);

  const openPicker = () => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
    } else {
      inputRef.current?.focus();
    }
  };

  return (
    <div>
      <label className={labelClass}>
        {label}
        {required ? " *" : ""}
      </label>

      <div
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            openPicker();
          }
        }}
        className={`group flex h-[56px] cursor-pointer items-center overflow-hidden rounded-xl border bg-white shadow-sm transition hover:border-blue-300 hover:shadow-md focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-50 dark:bg-slate-950 dark:focus-within:ring-blue-950 ${
          error
            ? "border-red-500"
            : "border-slate-200 dark:border-slate-700"
        }`}
      >
        <input
          ref={inputRef}
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-full min-w-0 flex-1 cursor-pointer bg-transparent px-4 text-sm font-medium text-slate-900 outline-none [color-scheme:light] [&::-webkit-calendar-picker-indicator]:hidden dark:text-slate-100 dark:[color-scheme:dark]"
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPicker();
          }}
          className="mr-2 grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-50 text-slate-600 transition group-hover:bg-blue-50 group-hover:text-blue-600 dark:bg-slate-800 dark:text-slate-300 dark:group-hover:bg-blue-950 dark:group-hover:text-blue-300"
          aria-label={`Open ${label} calendar`}
        >
          <FiCalendar size={20} />
        </button>
      </div>

      {error ? (
        <p className="mt-1.5 text-xs font-medium text-red-500">{error}</p>
      ) : null}
    </div>
  );
}

const AddContainerModal = ({
  isOpen,
  onClose,
  onSave,
  editingContainer,
  customers = [],
}) => {
  const [formData, setFormData] = useState({
    ...defaultFormData,
    date: getTodayInputDate(),
  });

  const [errors, setErrors] = useState({});
  const [apiMessage, setApiMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = checkIsSuperAdmin();

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    setApiMessage("");
    setErrors({});

    if (editingContainer) {
      setFormData({
        bookingBLNo: editingContainer.bookingBLNo || "",
        date:
          normalizeDateForInput(editingContainer.date) || getTodayInputDate(),
        containerNumber: editingContainer.containerNumber || "",
        vesselName: editingContainer.vesselName || "",
        origin: editingContainer.origin || "China",
        destination: editingContainer.destination || "UK",
        status:
          editingContainer.status === "Customs Cleared" ||
          editingContainer.status === "Custom Cleared"
            ? "Arrived at Port"
            : editingContainer.status || "In Transit",
        customCleared: Boolean(editingContainer.customCleared),
        containerType:
          editingContainer.containerType === "40ft High Cube"
            ? "40ft HC"
            : editingContainer.containerType || "40ft HC",
        shipper: editingContainer.shipper || "",
        consignee: editingContainer.consignee || "Meraki",
        customerId: editingContainer.customerId || "",
        expectedArrival: normalizeDateForInput(editingContainer.expectedArrival),
        deliveryBookedOn: normalizeDateForInput(
          editingContainer.deliveryBookedOn
        ),
        mainGoods: editingContainer.mainGoods || "",
        description: editingContainer.description || "",
      });
    } else {
      setFormData({
        ...defaultFormData,
        date: getTodayInputDate(),
      });
    }
  }, [editingContainer, isOpen]);

  const resetForm = () => {
    setFormData({
      ...defaultFormData,
      date: getTodayInputDate(),
    });
    setErrors({});
    setApiMessage("");
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.bookingBLNo?.trim()) {
      newErrors.bookingBLNo = "Booking/BL No is required";
    }

    if (!formData.containerNumber?.trim()) {
      newErrors.containerNumber = "Container number is required";
    }

    if (!formData.date) {
      newErrors.date = "Date is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const closeModal = () => {
    if (saving) return;

    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setApiMessage("");

    if (!validateForm()) return;

    const submitData = {
      bookingBLNo: formData.bookingBLNo.trim(),
      date: formData.date,
      containerNumber: formData.containerNumber.trim().toUpperCase(),
      vesselName: formData.vesselName.trim(),
      origin: formData.origin.trim(),
      destination: formData.destination.trim(),
      status: formData.status,
      customCleared: Boolean(formData.customCleared),
      containerType: formData.containerType || "40ft HC",
      consignee: formData.consignee.trim(),
      customerId: formData.customerId || null,
      expectedArrival: formData.expectedArrival || null,
      deliveryBookedOn: formData.deliveryBookedOn || null,
      mainGoods: formData.mainGoods.trim(),
      description: formData.description.trim(),
      voyageNumber: null,
    };

    if (isSuperAdmin) {
      submitData.shipper = formData.shipper.trim();
    }

    setSaving(true);

    try {
      await onSave(submitData);
      resetForm();
      onClose();
    } catch (err) {
      setApiMessage(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-950";

  const selectClass =
    "w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-3 pr-11 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-blue-950";

  const labelClass =
    "mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300";

  const errorInputClass =
    "border-red-500 focus:border-red-500 focus:ring-red-500/20";

  const modalContent = (
    <div className="fixed inset-0 z-[99999] flex min-h-screen w-screen items-center justify-center overflow-y-auto bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
      <div className="relative flex max-h-[calc(100vh-48px)] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="shrink-0 border-b border-slate-100 bg-white px-6 py-5 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                <FiBox className="text-xl" />
              </div>

              <div>
                <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                  {editingContainer ? "Edit Container" : "Add New Container"}
                </h2>

                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Fill in the container details below.
                </p>
              </div>
            </div>

            <button
              onClick={closeModal}
              disabled={saving}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Close modal"
              type="button"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <InlineMessage
              type="error"
              message={apiMessage}
              onClose={() => setApiMessage("")}
            />

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div>
                <label className={labelClass}>Booking/BL No *</label>

                <input
                  type="text"
                  value={formData.bookingBLNo}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      bookingBLNo: e.target.value,
                    })
                  }
                  className={`${inputClass} ${
                    errors.bookingBLNo ? errorInputClass : ""
                  }`}
                  placeholder="Booking / BL number"
                />

                {errors.bookingBLNo ? (
                  <p className="mt-1.5 text-xs font-medium text-red-500">
                    {errors.bookingBLNo}
                  </p>
                ) : null}
              </div>

              <DateField
                label="Date"
                required
                value={formData.date}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    date: value,
                  })
                }
                error={errors.date}
                labelClass={labelClass}
              />

              <div>
                <label className={labelClass}>Container Number *</label>

                <input
                  type="text"
                  value={formData.containerNumber}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      containerNumber: e.target.value.toUpperCase(),
                    })
                  }
                  className={`${inputClass} ${
                    errors.containerNumber ? errorInputClass : ""
                  }`}
                  placeholder="e.g., ABCD1234567"
                />

                {errors.containerNumber ? (
                  <p className="mt-1.5 text-xs font-medium text-red-500">
                    {errors.containerNumber}
                  </p>
                ) : null}
              </div>

              <div>
                <label className={labelClass}>Vessel Name</label>

                <input
                  type="text"
                  value={formData.vesselName}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vesselName: e.target.value,
                    })
                  }
                  className={inputClass}
                  placeholder="Vessel name"
                />
              </div>

              <SelectWithCustom
                label="Origin"
                value={formData.origin}
                options={originOptions}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    origin: value,
                  })
                }
                inputClass={inputClass}
                selectClass={selectClass}
                labelClass={labelClass}
                placeholder="Enter origin"
              />

              <SelectWithCustom
                label="Destination"
                value={formData.destination}
                options={destinationOptions}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    destination: value,
                  })
                }
                inputClass={inputClass}
                selectClass={selectClass}
                labelClass={labelClass}
                placeholder="Enter destination"
              />

              <div>
                <label className={labelClass}>Status</label>

                <div className="relative">
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value,
                      })
                    }
                    className={selectClass}
                  >
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                </div>
              </div>

              <div>
                <label className={labelClass}>Custom Cleared</label>

                <label className="flex h-[50px] items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.customCleared)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customCleared: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Mark as custom cleared
                </label>
              </div>

              <div>
                <label className={labelClass}>Container Type</label>

                <div className="relative">
                  <select
                    value={formData.containerType}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        containerType: e.target.value,
                      })
                    }
                    className={selectClass}
                  >
                    {containerTypeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>

                  <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                </div>
              </div>

              {isSuperAdmin ? (
                <div>
                  <label className={labelClass}>Shipper</label>

                  <input
                    type="text"
                    value={formData.shipper}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        shipper: e.target.value,
                      })
                    }
                    className={inputClass}
                    placeholder="Shipper name"
                  />
                </div>
              ) : null}

              <SelectWithCustom
                label="Consignee"
                value={formData.consignee}
                options={consigneeOptions}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    consignee: value,
                  })
                }
                inputClass={inputClass}
                selectClass={selectClass}
                labelClass={labelClass}
                placeholder="Enter consignee"
              />

              <div>
                <label className={labelClass}>Assign to Customer Optional</label>

                <div className="relative">
                  <select
                    value={formData.customerId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customerId: e.target.value,
                      })
                    }
                    className={selectClass}
                  >
                    <option value="">No customer assigned</option>

                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                        {customer.email ? ` (${customer.email})` : ""}
                      </option>
                    ))}
                  </select>

                  <FiChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                </div>
              </div>

              <DateField
                label="Expected Arrival"
                value={formData.expectedArrival}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    expectedArrival: value,
                  })
                }
                labelClass={labelClass}
              />

              <DateField
                label="Delivery Booked On"
                value={formData.deliveryBookedOn}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    deliveryBookedOn: value,
                  })
                }
                labelClass={labelClass}
              />

              <div className="md:col-span-2">
                <label className={labelClass}>Main Goods</label>

                <textarea
                  value={formData.mainGoods}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      mainGoods: e.target.value,
                    })
                  }
                  className={`${inputClass} min-h-[90px] resize-y`}
                  rows="3"
                  placeholder="Enter main goods details..."
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>Description</label>

                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      description: e.target.value,
                    })
                  }
                  className={`${inputClass} min-h-[96px] resize-y`}
                  rows="3"
                  placeholder="Additional details about the container..."
                />
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-slate-100 bg-white px-6 py-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={saving}
                className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <FiLoader className="animate-spin" />
                    Saving...
                  </>
                ) : editingContainer ? (
                  "Update Container"
                ) : (
                  "Add Container"
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddContainerModal;